import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Avatar,
  Divider,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Warning,
  CalendarToday,
  Schedule,
  People,
  TrendingDown,
  TrendingUp,
  Search,
  FilterList,
  Download,
  Refresh,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { EmployeeLeaveBalance, Employee } from '../types';
import { leaveBalanceService } from '../services/leaveBalanceService';
import { employeeService } from '../services/employeeService';
import { yearEndResetService } from '../services/yearEndResetService';

interface LeaveTrackingDashboardProps {
  className?: string;
}

interface LeaveAlert {
  employeeId: string;
  employeeName: string;
  department: string;
  leaveType: string;
  remaining: number;
  total: number;
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

const LeaveTrackingDashboard: React.FC<LeaveTrackingDashboardProps> = ({ className }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<EmployeeLeaveBalance[]>([]);
  const [filteredBalances, setFilteredBalances] = useState<EmployeeLeaveBalance[]>([]);
  const [alerts, setAlerts] = useState<LeaveAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [alertFilter, setAlertFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [error, setError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Philippine Standard Leave Types based on Labor Code and Republic Acts
  const leaveTypes = [
    { 
      key: 'vacation', 
      label: 'Vacation Leave', 
      description: '5 days annual vacation leave', 
      color: 'primary',
      totalDays: 5,
      legal: 'Company Policy'
    },
    { 
      key: 'sick', 
      label: 'Sick Leave', 
      description: '5 days annual sick leave', 
      color: 'error',
      totalDays: 5,
      legal: 'Company Policy'
    },
    { 
      key: 'maternity', 
      label: 'Maternity Leave', 
      description: '105 days paid leave for childbirth (Female only)', 
      color: 'secondary',
      totalDays: 105,
      legal: 'RA 11210 - Expanded Maternity Leave Law',
      gender: 'female'
    },
    { 
      key: 'paternity', 
      label: 'Paternity Leave', 
      description: '7 days paid leave for fathers (Male only)', 
      color: 'success',
      totalDays: 7,
      legal: 'RA 8187 - Paternity Leave Act',
      gender: 'male'
    }
   ];

  // Function to get applicable leave types for an employee based on gender
  const getApplicableLeaveTypes = (employeeGender: string | null) => {
    return leaveTypes.filter(leaveType => {
      if (!leaveType.gender) return true; // No gender restriction
      return leaveType.gender === employeeGender;
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAndSortData();
  }, [leaveBalances, employees, searchTerm, departmentFilter, alertFilter, sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check and perform automatic year-end reset if needed
      const resetCheck = await yearEndResetService.checkAndPerformYearEndReset();
      if (resetCheck.resetPerformed) {
        setResetMessage(resetCheck.message);
      }
      
      const currentYear = new Date().getFullYear();
      const [employeesData, balancesData] = await Promise.all([
        employeeService.getAll(),
        leaveBalanceService.getAllEmployeeLeaveBalances(currentYear)
      ]);
      
      setEmployees(employeesData);
      setLeaveBalances(balancesData);
      generateAlerts(balancesData, employeesData);
    } catch (err) {
      console.error('Error loading leave tracking data:', err);
      setError('Failed to load leave tracking data');
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = (balances: EmployeeLeaveBalance[], employees: Employee[]) => {
    const newAlerts: LeaveAlert[] = [];
    
    balances.forEach(balance => {
      const employee = employees.find(emp => emp.id === balance.employee_id);
      if (!employee) return;
      
      const employeeName = `${employee.first_name} ${employee.last_name}`;
      
      leaveTypes.forEach(type => {
        const remaining = balance[`${type.key}_days_remaining` as keyof EmployeeLeaveBalance] as number || 0;
        const total = balance[`${type.key}_days_total` as keyof EmployeeLeaveBalance] as number || 0;
        const used = balance[`${type.key}_days_used` as keyof EmployeeLeaveBalance] as number || 0;
        
        if (total === 0) return;
        
        // Critical alerts
        if (remaining <= 0) {
          newAlerts.push({
            employeeId: balance.employee_id,
            employeeName,
            department: employee.department,
            leaveType: type.label,
            remaining,
            total,
            severity: 'critical',
            message: `${type.label} days exhausted (${remaining} remaining)`
          });
        }
        // Overuse alerts
        else if (used > total) {
          newAlerts.push({
            employeeId: balance.employee_id,
            employeeName,
            department: employee.department,
            leaveType: type.label,
            remaining,
            total,
            severity: 'critical',
            message: `${type.label} days overused (${used}/${total} used)`
          });
        }
        // Warning alerts
        else if (remaining <= 2 && remaining > 0) {
          newAlerts.push({
            employeeId: balance.employee_id,
            employeeName,
            department: employee.department,
            leaveType: type.label,
            remaining,
            total,
            severity: 'warning',
            message: `Low ${type.label} balance (${remaining} days remaining)`
          });
        }
      });
    });
    
    setAlerts(newAlerts);
  };

  const filterAndSortData = () => {
    let filtered = [...leaveBalances];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(balance => {
        const employee = employees.find(emp => emp.id === balance.employee_id);
        if (!employee) return false;
        
        const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
        const department = employee.department.toLowerCase();
        const search = searchTerm.toLowerCase();
        
        return fullName.includes(search) || department.includes(search);
      });
    }
    
    // Filter by department
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(balance => {
        const employee = employees.find(emp => emp.id === balance.employee_id);
        return employee?.department === departmentFilter;
      });
    }
    
    // Filter by alert status
    if (alertFilter !== 'all') {
      filtered = filtered.filter(balance => {
        const employeeAlerts = getEmployeeAlerts(balance.employee_id);
        
        switch (alertFilter) {
          case 'critical':
            return employeeAlerts.some(alert => alert.severity === 'critical');
          case 'warning':
            return employeeAlerts.some(alert => alert.severity === 'warning');
          case 'none':
            return employeeAlerts.length === 0;
          default:
            return true;
        }
      });
    }
    
    // Sort data
    filtered.sort((a, b) => {
      const empA = employees.find(emp => emp.id === a.employee_id);
      const empB = employees.find(emp => emp.id === b.employee_id);
      
      if (!empA || !empB) return 0;
      
      switch (sortBy) {
        case 'name':
          return `${empA.first_name} ${empA.last_name}`.localeCompare(`${empB.first_name} ${empB.last_name}`);
        case 'department':
          return empA.department.localeCompare(empB.department);
        case 'alerts':
          const alertsA = getEmployeeAlerts(a.employee_id).length;
          const alertsB = getEmployeeAlerts(b.employee_id).length;
          return alertsB - alertsA;
        default:
          return 0;
      }
    });
    
    setFilteredBalances(filtered);
  };

  const getEmployeeAlerts = (employeeId: string): LeaveAlert[] => {
    return alerts.filter(alert => alert.employeeId === employeeId);
  };

  const getDepartments = (): string[] => {
    const departments = employees.map(emp => emp.department);
    return [...new Set(departments)].sort();
  };

  const exportData = () => {
    const csvData = filteredBalances.map(balance => {
      const employee = employees.find(emp => emp.id === balance.employee_id);
      const employeeAlerts = getEmployeeAlerts(balance.employee_id);
      
      return {
        'Employee Name': employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown',
        'Department': employee?.department || 'Unknown',
        'Vacation Remaining': balance.vacation_days_remaining || 0,
        'Sick Remaining': balance.sick_days_remaining || 0,
        'Personal Remaining': balance.personal_days_remaining || 0,
        'Emergency Remaining': balance.emergency_days_remaining || 0,
        'Maternity Remaining': balance.maternity_days_remaining || 0,
        'Paternity Remaining': balance.paternity_days_remaining || 0,
        'Alert Count': employeeAlerts.length,
        'Critical Alerts': employeeAlerts.filter(a => a.severity === 'critical').length,
        'Warning Alerts': employeeAlerts.filter(a => a.severity === 'warning').length
      };
    });
    
    const csv = [
      Object.keys(csvData[0] || {}).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-tracking-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleYearEndReset = async () => {
    try {
      setResetLoading(true);
      setResetMessage(null);
      
      const currentYear = new Date().getFullYear();
      const result = await yearEndResetService.manualYearEndReset(currentYear);
      
      if (result.success) {
        setResetMessage(`✅ Successfully reset leave balances for ${result.employeesProcessed} employees`);
        // Reload data to show updated balances
        await loadData();
      } else {
        setResetMessage(`❌ Reset failed: ${result.message}`);
      }
    } catch (error) {
      setResetMessage(`❌ Reset failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setResetLoading(false);
    }
  };

  const getOverallStats = () => {
    const totalEmployees = leaveBalances.length;
    const criticalAlerts = alerts.filter(alert => alert.severity === 'critical').length;
    const warningAlerts = alerts.filter(alert => alert.severity === 'warning').length;
    const employeesWithAlerts = new Set(alerts.map(alert => alert.employeeId)).size;
    const healthyEmployees = totalEmployees - employeesWithAlerts;
    
    return {
      totalEmployees,
      healthyEmployees,
      criticalAlerts,
      warningAlerts,
      employeesWithAlerts
    };
  };

  const stats = getOverallStats();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 2 }}>Loading leave tracking data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderColor: 'error.light' }}>
        <AlertTitle>Error</AlertTitle>
        {error}
        <Button onClick={loadData} sx={{ mt: 2 }} startIcon={<Refresh />}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', minHeight: '100vh' }} className={className}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { lg: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Leave Tracking Dashboard
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mt: 1 }}>
              Monitor employee leave balances and alerts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={loadData}
              variant="outlined"
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
            <Button
              onClick={handleYearEndReset}
              variant="outlined"
              color="warning"
              disabled={resetLoading}
              startIcon={resetLoading ? <CircularProgress size={16} /> : <CalendarToday />}
            >
              {resetLoading ? 'Resetting...' : 'Year-End Reset'}
            </Button>
            <Button
              onClick={exportData}
              variant="contained"
              startIcon={<Download />}
            >
              Export Data
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Reset Message */}
      {resetMessage && (
        <Alert 
          severity={resetMessage.includes('✅') ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          onClose={() => setResetMessage(null)}
        >
          {resetMessage}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
          <Card sx={{ borderLeft: 4, borderColor: 'primary.main', '&:hover': { boxShadow: 3 } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Total Employees
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'text.primary', mt: 1 }}>
                    {stats.totalEmployees}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                  <People sx={{ color: 'primary.main' }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
           <Card sx={{ borderLeft: 4, borderColor: 'success.main', '&:hover': { boxShadow: 3 } }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Box>
                   <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                     Healthy
                   </Typography>
                   <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'success.main', mt: 1 }}>
                     {stats.healthyEmployees}
                   </Typography>
                 </Box>
                 <Avatar sx={{ bgcolor: 'success.light', width: 48, height: 48 }}>
                   <CheckCircle sx={{ color: 'success.main' }} />
                 </Avatar>
               </Box>
             </CardContent>
           </Card>
         </Grid>
         
         <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
           <Card sx={{ borderLeft: 4, borderColor: 'warning.main', '&:hover': { boxShadow: 3 } }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Box>
                   <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                     Warnings
                   </Typography>
                   <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'warning.main', mt: 1 }}>
                     {stats.warningAlerts}
                   </Typography>
                 </Box>
                 <Avatar sx={{ bgcolor: 'warning.light', width: 48, height: 48 }}>
                   <Warning sx={{ color: 'warning.main' }} />
                 </Avatar>
               </Box>
             </CardContent>
           </Card>
         </Grid>
         
         <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
           <Card sx={{ borderLeft: 4, borderColor: 'error.main', '&:hover': { boxShadow: 3 } }}>
             <CardContent sx={{ p: 3 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Box>
                   <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                     Critical
                   </Typography>
                   <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'error.main', mt: 1 }}>
                     {stats.criticalAlerts}
                   </Typography>
                 </Box>
                 <Avatar sx={{ bgcolor: 'error.light', width: 48, height: 48 }}>
                   <Cancel sx={{ color: 'error.main' }} />
                 </Avatar>
               </Box>
             </CardContent>
           </Card>
         </Grid>
         
         <Grid size={{ xs: 12, md: 6, lg: 2.4 }}>
          <Card sx={{ borderLeft: 4, borderColor: 'secondary.main', '&:hover': { boxShadow: 3 } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                    With Alerts
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main', mt: 1 }}>
                    {stats.employeesWithAlerts}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.light', width: 48, height: 48 }}>
                  <Warning sx={{ color: 'secondary.main' }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', color: 'text.primary', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          Filters & Search
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary', mb: 1 }}>Search Employees</Typography>
            <TextField
              placeholder="Search by name or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary', mb: 1 }}>Department</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Departments</MenuItem>
                {getDepartments().map(dept => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary', mb: 1 }}>Alert Status</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={alertFilter}
                onChange={(e) => setAlertFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Employees</MenuItem>
                <MenuItem value="critical">Critical Alerts</MenuItem>
                <MenuItem value="warning">Warning Alerts</MenuItem>
                <MenuItem value="none">No Alerts</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>Sort by:</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="department">Department</MenuItem>
                <MenuItem value="alerts">Alert Count</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Showing {filteredBalances.length} of {leaveBalances.length} employees
          </Typography>
        </Box>
      </Paper>

      {/* Employee Leave Balances */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
          Employee Leave Balances
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Detailed view of each employee's leave status and alerts
        </Typography>
        
        <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2 }}>
          <Table sx={{ minWidth: 650 }} aria-label="employee leave balances table">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Employee</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Department</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Gender</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Vacation Leave (5 days)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Sick Leave (5 days)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Maternity (105 days)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Paternity (7 days)</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBalances.map(balance => {
                const employee = employees.find(emp => emp.id === balance.employee_id);
                const employeeAlerts = alerts.filter(alert => alert.employeeId === balance.employee_id);
                
                if (!employee) return null;
                
                const hasCritical = employeeAlerts.some(alert => alert.severity === 'critical');
                const hasWarning = employeeAlerts.some(alert => alert.severity === 'warning');
                
                return (
                  <TableRow 
                    key={balance.employee_id}
                    sx={{ 
                      '&:hover': { bgcolor: 'action.hover' },
                      borderLeft: 4,
                      borderColor: hasCritical ? 'error.main' : hasWarning ? 'warning.main' : 'transparent'
                    }}
                  >
                    {/* Employee Info */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{
                          width: 40,
                          height: 40,
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                          fontSize: '0.9rem',
                          fontWeight: 'bold'
                        }}>
                          {employee.first_name[0]}{employee.last_name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            {employee.first_name} {employee.last_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            ID: {employee.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    {/* Department */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {employee.department}
                      </Typography>
                    </TableCell>
                    
                    {/* Gender */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'medium',
                        color: employee.gender === 'male' ? 'primary.main' : employee.gender === 'female' ? 'secondary.main' : 'text.secondary',
                        textTransform: 'capitalize'
                      }}>
                        {employee.gender || 'Not Set'}
                      </Typography>
                    </TableCell>
                    
                    {/* Vacation Leave */}
                     <TableCell align="center" sx={{ py: 2 }}>
                       {(() => {
                         const remaining = balance.vacation_days_remaining || 0;
                         const total = balance.vacation_days_total || 5;
                         const used = balance.vacation_days_used || 0;
                         const isLow = remaining <= 1;
                         const isCritical = remaining <= 0;
                         
                         return (
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="body2" sx={{ 
                               fontWeight: 'bold',
                               color: isCritical ? 'error.main' : isLow ? 'warning.main' : 'text.primary'
                             }}>
                               {remaining}/{total}
                             </Typography>
                             <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                               {used} used
                             </Typography>
                           </Box>
                         );
                       })()}
                     </TableCell>
                    
                    {/* Sick Leave */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      {(() => {
                        const remaining = balance.sick_days_remaining || 0;
                        const total = balance.sick_days_total || 5;
                        const used = balance.sick_days_used || 0;
                        const isLow = remaining <= 1;
                        const isCritical = remaining <= 0;
                        
                        return (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 'bold',
                              color: isCritical ? 'error.main' : isLow ? 'warning.main' : 'text.primary'
                            }}>
                              {remaining}/{total}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {used} used
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    
                    {/* Maternity Leave - Female Only */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      {employee.gender === 'female' ? (() => {
                        const remaining = balance.maternity_days_remaining || 0;
                        const total = balance.maternity_days_total || 105;
                        const used = balance.maternity_days_used || 0;
                        const isLow = remaining <= 10;
                        const isCritical = remaining <= 0;
                        
                        return (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 'bold',
                              color: isCritical ? 'error.main' : isLow ? 'warning.main' : 'text.primary'
                            }}>
                              {remaining}/{total}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {used} used
                            </Typography>
                          </Box>
                        );
                      })() : (
                        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    
                    {/* Paternity Leave - Male Only */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      {employee.gender === 'male' ? (() => {
                        const remaining = balance.paternity_days_remaining || 0;
                        const total = balance.paternity_days_total || 7;
                        const used = balance.paternity_days_used || 0;
                        const isLow = remaining <= 1;
                        const isCritical = remaining <= 0;
                        
                        return (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 'bold',
                              color: isCritical ? 'error.main' : isLow ? 'warning.main' : 'text.primary'
                            }}>
                              {remaining}/{total}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {used} used
                            </Typography>
                          </Box>
                        );
                      })() : (
                        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    
                    {/* Status */}
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {employeeAlerts.length > 0 ? (
                          <Chip
                            label={`${employeeAlerts.length} alert${employeeAlerts.length !== 1 ? 's' : ''}`}
                            color={hasCritical ? 'error' : 'warning'}
                            size="small"
                            variant="filled"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip
                            label="Good"
                            color="success"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                        {employeeAlerts.length > 0 && (
                          <Tooltip title={employeeAlerts.map(alert => alert.message).join(', ')}>
                            <IconButton size="small">
                              <Warning sx={{ fontSize: 16, color: hasCritical ? 'error.main' : 'warning.main' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
            );
          })}
             </TableBody>
           </Table>
         </TableContainer>
         
         {filteredBalances.length === 0 && (
           <Paper sx={{ p: 8, textAlign: 'center', mt: 3 }}>
             <Avatar sx={{ 
               width: 80, 
               height: 80, 
               bgcolor: 'grey.100', 
               mx: 'auto', 
               mb: 3 
             }}>
               <People sx={{ fontSize: 40, color: 'grey.400' }} />
             </Avatar>
             <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
               No employees found
             </Typography>
             <Typography variant="body1" sx={{ 
               color: 'text.secondary', 
               mb: 4, 
               maxWidth: 400, 
               mx: 'auto' 
             }}>
               We couldn't find any employees matching your current search and filter criteria.
             </Typography>
             <Button 
               onClick={() => {
                 setSearchTerm('');
                 setDepartmentFilter('all');
                    setAlertFilter('all');
               }}
               variant="outlined"
               sx={{ mx: 'auto' }}
             >
               Clear all filters
             </Button>
           </Paper>
          )}
      </Paper>
    </Box>
  );
};

export default LeaveTrackingDashboard;